<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="a"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <form :action="meta.links.index" method="GET" @submit.prevent="filter">
            <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                <div class="sm:col-span-8">
                    <label class="block text-sm font-medium leading-5 text-gray-700">{{ $t('messages.search') }}</label>
                    <div class="mt-1 relative rounded-md shadow-sm">
                        <input
                            v-model="form.search"
                            type="text"
                            class="pr-10 border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm w-full"
                        />
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                            <svg class="h-5 w-5 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fill-rule="evenodd"
                                    d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                    clip-rule="evenodd"
                                ></path>
                            </svg>
                        </div>
                    </div>
                </div>

                <div class="sm:col-span-2">
                    <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                    <div class="mt-1">
                        <SubmitFormButton
                            :button-text="$t('messages.filter')"
                            :processing="form.processing"
                            is-filter
                        />
                    </div>
                </div>
            </div>
        </form>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.title') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.levels', 2) }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    -
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $tc('messages.grade_levels', 2) }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-right text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    <span class="sr-only">{{ $t('messages.actions') }}</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                                v-for="(gamificationSet, index) in gamificationSets.data"
                                :key="gamificationSet.id"
                            >
                                <td
                                    class="max-w-sm truncate px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900"
                                >
                                    {{ gamificationSet.title }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ gamificationSet.levels_count }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex gap-2">
                                        <div
                                            :title="`${gamificationSet.count.rewards} ${$tc(
                                                'messages.gamification_rewards',
                                                gamificationSet.count.rewards,
                                            )}`"
                                        >
                                            <GamificationSectionIcon
                                                :class="
                                                    gamificationSet.count.rewards ? 'text-indigo-400' : 'text-gray-400'
                                                "
                                                class="text-gray-400 h-4 w-4"
                                                section="rewards"
                                            />
                                        </div>
                                        <div
                                            :title="`${gamificationSet.count.items} ${$tc(
                                                'messages.gamification_items',
                                                gamificationSet.count.items,
                                            )}`"
                                        >
                                            <GamificationSectionIcon
                                                :class="
                                                    gamificationSet.count.items ? 'text-indigo-400' : 'text-gray-400'
                                                "
                                                class="text-gray-400 h-4 w-4"
                                                section="items"
                                            />
                                        </div>
                                        <div
                                            :title="`${gamificationSet.count.badges} ${$tc(
                                                'messages.badges',
                                                gamificationSet.count.badges,
                                            )}`"
                                        >
                                            <GamificationSectionIcon
                                                :class="
                                                    gamificationSet.count.badges ? 'text-indigo-400' : 'text-gray-400'
                                                "
                                                class="text-gray-400 h-4 w-4"
                                                section="badges"
                                            />
                                        </div>
                                        <div
                                            :title="`${gamificationSet.count.card_collections} ${$tc(
                                                'messages.card_collections',
                                                gamificationSet.count.card_collections,
                                            )}`"
                                        >
                                            <GamificationSectionIcon
                                                :class="
                                                    gamificationSet.count.card_collections
                                                        ? 'text-indigo-400'
                                                        : 'text-gray-400'
                                                "
                                                class="text-gray-400 h-4 w-4"
                                                section="card_collections"
                                            />
                                        </div>
                                        <div
                                            :title="`${gamificationSet.count.character_types} ${$tc(
                                                'messages.gamification_character_types',
                                                gamificationSet.count.character_types,
                                            )}`"
                                        >
                                            <GamificationSectionIcon
                                                :class="
                                                    gamificationSet.count.character_types
                                                        ? 'text-indigo-400'
                                                        : 'text-gray-400'
                                                "
                                                class="text-gray-400 h-4 w-4"
                                                section="character_types"
                                            />
                                        </div>
                                        <div
                                            :title="`${gamificationSet.count.levels} ${$tc(
                                                'messages.levels',
                                                gamificationSet.count.levels,
                                            )}`"
                                        >
                                            <GamificationSectionIcon
                                                :class="
                                                    gamificationSet.count.levels ? 'text-indigo-400' : 'text-gray-400'
                                                "
                                                class="text-gray-400 h-4 w-4"
                                                section="levels"
                                            />
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    {{ gamificationSet.grade_levels }}
                                </td>
                                <td
                                    class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500 flex justify-end space-x-1"
                                >
                                    <InertiaSquareLinkButton action="show" :href="gamificationSet.links.show" />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import GamificationSectionIcon from '@/components/Gamification/GamificationSectionIcon.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        GamificationSectionIcon,
        PanelPage,
        InertiaSquareLinkButton,
    },
    props: ['meta', 'gamificationSets', 'options', 'filters'],
    layout: Layout,
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
            }),
        };
    },
    name: 'Index',
    computed: {},
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
